import { useCallback } from 'react';

import { queryOptions, useQuery, useQueryClient } from '@tanstack/react-query';

import { CostReportAllocationGroupTotalCostItem } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ALLOCATION_GROUPS_TOTAL_COSTS_QUERY_KEY = 'allocation-groups-total-costs';

type UseAllocationGroupsTotalCostsQuery = {
  allocationGroupsTotalCosts?: CostReportAllocationGroupTotalCostItem[];
  isLoading: boolean;
  error?: unknown;
  refetch?: () => void;
  invalidate(): void;
};

type UseAllocationGroupsTotalCostsQueryProps = {
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  clusterIds?: string[];
  pageLimit?: number;
  includeIdleCosts?: boolean;
  enabled?: boolean;
};

const getAllocationGroupsTotalCostsQueryKey = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  pageLimit,
  includeIdleCosts,
}: Partial<
  Pick<
    UseAllocationGroupsTotalCostsQueryProps,
    | 'startTime'
    | 'endTime'
    | 'useListingPrices'
    | 'clusterIds'
    | 'includeIdleCosts'
    | 'pageLimit'
  >
> = {}) =>
  [
    ALLOCATION_GROUPS_TOTAL_COSTS_QUERY_KEY,
    ...(startTime !== undefined
      ? [
          startTime,
          endTime,
          useListingPrices,
          clusterIds,
          pageLimit,
          includeIdleCosts,
        ]
      : []),
  ] as const;

const getAllocationGroupsTotalCostsQueryOptions = ({
  startTime,
  endTime,
  useListingPrices,
  clusterIds,
  pageLimit = 5,
  includeIdleCosts = false,
  enabled = true,
}: UseAllocationGroupsTotalCostsQueryProps) =>
  queryOptions({
    queryKey: getAllocationGroupsTotalCostsQueryKey({
      startTime,
      endTime,
      useListingPrices,
      clusterIds,
      pageLimit,
      includeIdleCosts,
    }),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getAllocationGroupTotalCosts({
        clusterIds,
        startTime,
        endTime,
        useListingPrices,
        pageLimit: pageLimit.toString(),
        includeIdleResourceCosts: includeIdleCosts,
      });
      return data.items;
    },
    enabled: Boolean(enabled && startTime && endTime),
    refetchOnWindowFocus: false,
  });

export const useAllocationGroupsTotalCostsQuery = (
  props: UseAllocationGroupsTotalCostsQueryProps
): UseAllocationGroupsTotalCostsQuery => {
  const queryClient = useQueryClient();

  const {
    isLoading,
    data: allocationGroupsTotalCosts,
    error,
    refetch,
  } = useQuery(getAllocationGroupsTotalCostsQueryOptions(props));

  const invalidate = useCallback(
    () =>
      queryClient.invalidateQueries({
        queryKey: getAllocationGroupsTotalCostsQueryKey(),
      }),
    [queryClient]
  );

  return {
    allocationGroupsTotalCosts,
    isLoading,
    error,
    refetch,
    invalidate,
  };
};
